import { Copy } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StepVertical from '@/domains/mobile-web/components/StepVertical/StepVertical';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import { useOrderTracking } from '@/domains/mobile-web/hooks/useOrders';
import { isNotFound } from '@/domains/mobile-web/hooks/useProducts';
import { formatAddress } from '@/domains/mobile-web/utils/address';
import { estimatedArrival, toTrackingSteps } from '@/domains/mobile-web/utils/tracking';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard?.writeText(value);
  } catch {}
};

const TransactionTrack = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const cartCount = useCartCount();
  const [copied, setCopied] = useState<string | null>(null);
  const { data, isLoading, error } = useOrderTracking(id);

  const handleCopy = (value: string) => {
    copyToClipboard(value);
    setCopied(value);
    window.setTimeout(() => setCopied((prev) => (prev === value ? null : prev)), 1500);
  };

  const shipment = data?.shipment ?? null;
  const address = data?.shipping_address;
  const trackingNumber = shipment?.tracking_number ?? null;
  const steps = toTrackingSteps(shipment?.events ?? []);
  const arrival = shipment ? estimatedArrival(shipment) : null;

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader
          type="textBucket"
          onBack={() => navigate(-1)}
          totalProduct={cartCount}
          onAction={() => navigate('/store/cart')}
          title="Lacak Pengiriman"
        />
      </HeaderMobile>

      <WrapperMobile>
        {!id || isNotFound(error) ? (
          <p className="body1 text-text-grey text-center py-6">Pesanan tidak ditemukan.</p>
        ) : isLoading ? (
          <p className="body1 text-text-grey text-center py-6">Memuat pengiriman...</p>
        ) : !data ? (
          <p className="body1 text-text-grey text-center py-6">Gagal memuat pengiriman.</p>
        ) : (
          <>
            <div className="shadowCard p-3 flex flex-col gap-3">
              <h3 className="title2medium text-black">Informasi Pengiriman</h3>
              <div className="grid grid-cols-[100px_1fr] gap-3 justify-between">
                <span className="reg12 text-text-grey">Pengiriman</span>
                <span>
                  {shipment
                    ? [
                        shipment.courier_name ?? shipment.courier_code.toUpperCase(),
                        shipment.service_name,
                      ]
                        .filter(Boolean)
                        .join(' - ')
                    : '-'}
                </span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-3 justify-between">
                <span className="reg12 text-text-grey">Nomor Resi</span>
                {trackingNumber ? (
                  <button
                    type="button"
                    onClick={() => handleCopy(trackingNumber)}
                    aria-label={`Salin nomor resi ${trackingNumber}`}
                    className="flex w-fit shrink-0 items-center gap-1 body1medium text-text-blue-100 transition hover:opacity-80"
                  >
                    <Copy size={14} strokeWidth={1.8} />
                    <span>{copied === trackingNumber ? 'Tersalin' : trackingNumber}</span>
                  </button>
                ) : (
                  <span>-</span>
                )}
              </div>
              {address && (
                <div className="grid grid-cols-[100px_1fr] gap-3 justify-between">
                  <span className="reg12 text-text-grey">Alamat </span>
                  <div className="text-text-grey">
                    <p className="text-black">{address.recipient_name}</p>
                    <p>{address.phone_number}</p>
                    <p>{formatAddress(address)}</p>
                    {address.postal_code && <p>{address.postal_code}</p>}
                  </div>
                </div>
              )}
            </div>
            <div className="shadowCard p-3  items-center w-full gap-3 justify-between inline-flex flex-wrap">
              <div className="rounded-[6px] w-fit reg12 px-3 py-1 bg-text-blue-10">
                <span>{steps.activeTitle}</span>
              </div>
              {arrival && (
                <div className="flex flex-col items-end justify-end text-right flex-1 basis-[200px]">
                  <p>Estimasi Tiba</p>
                  <p className="title1medium">{arrival}</p>
                </div>
              )}
            </div>

            <StepVertical items={steps.items} activeIndex={steps.activeIndex} />
          </>
        )}
      </WrapperMobile>
    </main>
  );
};

export default TransactionTrack;
